'use client';
import { useState } from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useToast } from '@practium/ui';
import { useCancelAnonymization } from '@practium/features/api';
import type { AnonymizationPendingError } from '@practium/features/api';
import { AuthButton } from '../AuthButton/AuthButton';
import { AuthInput } from '../AuthInput/AuthInput';
import styles from './AnonymizationGate.module.css';

export interface AnonymizationGateCredentials {
	email: string;
	password: string;
}

interface AnonymizationGateProps {
	info: AnonymizationPendingError;
	credentials?: AnonymizationGateCredentials | null;
	onReactivated: (credentials: AnonymizationGateCredentials) => void;
	onClose: () => void;
}

const formatDeadline = (value: string | null) => {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

export const AnonymizationGate = ({
	info,
	credentials,
	onReactivated,
	onClose,
}: AnonymizationGateProps) => {
	const toast = useToast();
	const { mutateAsync: cancelAnonymization, isPending } = useCancelAnonymization();
	const [email, setEmail] = useState(credentials?.email ?? '');
	const [password, setPassword] = useState(credentials?.password ?? '');
	const [showCredentials, setShowCredentials] = useState(!credentials);

	const canReactivate = info.remainingDays > 0;
	const deadline = formatDeadline(info.canCancelUntil);
	const remainingText = info.remainingDays === 1 ? '1 dia' : `${info.remainingDays} dias`;

	const handleReactivate = async () => {
		if (!email || !password) {
			toast.error('Informe seu e-mail e senha para reativar a conta.');
			setShowCredentials(true);
			return;
		}
		try {
			const result = await cancelAnonymization({ email, password });
			toast.success(result.message || 'Conta reativada com sucesso!');
			onReactivated({ email, password });
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao reativar a conta.');
			setShowCredentials(true);
		}
	};

	return (
		<div className={styles.overlay}>
			<div className={styles.modal}>
				<div className={styles.header}>
					<WarningAmberOutlinedIcon className={styles.warningIcon} />
					<h2 className={styles.title}>Conta em processo de exclusão</h2>
				</div>
				<div className={styles.body}>
					{canReactivate ? (
						<>
							<p className={styles.text}>
								Esta conta está bloqueada porque existe uma solicitação de exclusão pendente. Os
								seus dados ainda não foram removidos e você pode reativar a conta se mudou de
								ideia.
							</p>
							<p className={styles.deadline}>
								Prazo para reativar: <strong>{remainingText}</strong>
								{deadline ? (
									<>
										{' '}
										(até <strong>{deadline}</strong>)
									</>
								) : null}
								. Após esse prazo, a exclusão será concluída e não poderá ser desfeita.
							</p>
							{showCredentials && (
								<div className={styles.credentials}>
									<p className={styles.text}>
										Para reativar, confirme o e-mail e a senha da conta:
									</p>
									<AuthInput
										label="E-mail"
										type="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder="exemplo@gmail.com"
										disabled={isPending}
									/>
									<AuthInput
										label="Senha"
										isPassword
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										placeholder="Senha atual"
										disabled={isPending}
									/>
								</div>
							)}
						</>
					) : (
						<p className={styles.text}>
							O prazo para reativar esta conta terminou. A exclusão dos dados será concluída em
							breve e não pode mais ser cancelada.
						</p>
					)}
				</div>
				<div className={styles.actions}>
					<button type="button" className={styles.backButton} onClick={onClose} disabled={isPending}>
						Voltar
					</button>
					{canReactivate && (
						<AuthButton onClick={handleReactivate} isLoading={isPending} disabled={isPending}>
							Quero manter minha conta
						</AuthButton>
					)}
				</div>
			</div>
		</div>
	);
};
